import type { PaletteModes } from '@allenai/varnish-theme/mui';
import varnishTokens from '@allenai/varnish-theme/mui';
import { type Theme } from '@mui/material';
import type { TypographyVariant } from '@mui/material/styles';

// importing from @mui/system directly seems to get us around some client component issues in Next
import { getContrastRatio } from '@mui/system/colorManipulator';

// Used to convert style object to css for typographic elements like h1...h6
export const getTypographyStyle = (
    theme: Omit<Theme, 'components'>,
    key: TypographyVariant,
    includeKey: boolean = true
) => {
    const typography = (
        theme.typography as unknown as Record<string, Record<string, string | number | undefined>>
    )[key];
    return `
    ${includeKey ? `${key} {` : ''}
      color: ${typography.color};
      font-family: ${typography.fontFamily};
      font-weight:  ${typography.fontWeight};
      font-size:  ${typography.fontSize};
      line-height:  ${typography.lineHeight};
      margin:  ${typography.margin};
    ${includeKey ? '}' : ''}
  `;
};

export const getThemeObjectTokens = <T extends { [index: string]: { value: string | number } }>(
    themeObject: T,
    properties: (keyof T)[] = Object.keys(themeObject)
) => {
    type RecordOfT = Record<keyof T, string>;

    return properties.reduce((acc: RecordOfT, attribute: keyof T) => {
        acc[attribute] = themeObject[attribute].value.toString();
        return acc;
    }, {} as RecordOfT);
};

interface ColorWithContrastRatio {
    color: string;
    contrastRatio: number;
}
const getColorWithContrastRatio = (baseColor: string, color: string): ColorWithContrastRatio => {
    return {
        color,
        contrastRatio: getContrastRatio(baseColor, color),
    };
};

/**
 *
 * @param backgroundColor The color you want to get the contrast color for
 * @param mode 'light' or 'dark', this determines which palette we pull text colors from
 * @returns The primary or reversed value for the palette mode selected, chosen based on whichever has the better contrast ratio
 */
export const getContrastText = (
    backgroundColor: string,
    mode: PaletteModes = 'light',
    desiredContrastRatio = 4.5
) => {
    const primaryText = getColorWithContrastRatio(
        backgroundColor,
        varnishTokens.palette[mode].text.primary.default.value
    );

    const reversedText = getColorWithContrastRatio(
        backgroundColor,
        varnishTokens.palette[mode].text.primary.reversed.value
    );

    const secondaryText = getColorWithContrastRatio(
        backgroundColor,
        varnishTokens.palette[mode].text.secondary.default.value
    );

    // HACK: dark-green will always have better contrast than ai2-green. This lets us keep using ai2-green if it's above the contrast ratio we're looking for
    if (primaryText.contrastRatio >= desiredContrastRatio) {
        return primaryText.color;
    }

    // We want the color with the highest contrast ratio at the first index
    const colorsOrderedByContrastRatio = [primaryText, reversedText, secondaryText].sort(
        (a, b) => b.contrastRatio - a.contrastRatio
    );

    return colorsOrderedByContrastRatio[0].color;
};

export const isThemeColor = (theme: Theme, key: string): key is keyof typeof theme.color => {
    return key in theme.color;
};
